// src/components/BucksAdminAnnouncements.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import '../stylings/BucksAdminDashboard.css';
import '../stylings/DeleteConfirmationModal.css';
import AnnouncementFormModal from './AnnouncementFormModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

function BucksAdminAnnouncements() {
  // Get authentication context
  const { adminUser } = useAuth();
  
  // State for announcements
  const [announcements, setAnnouncements] = useState([]);
  // State for modal visibility (replacing showForm)
  const [isModalOpen, setIsModalOpen] = useState(false);
  // State for editing
  const [editingId, setEditingId] = useState(null);
  // State for search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  // State for loading
  const [loading, setLoading] = useState(true);
  // State for notification
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  // State for form data
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    body: '',
    tag: 'Update',
    image_url: '',
    publish_start: '',
    publish_end: '',
    status: 'draft',
    priority: 5
  });

  // Load announcements data from API
  useEffect(() => {
    setLoading(true);
    
    // Construct query parameters based on filters
    const queryParams = new URLSearchParams();
    if (statusFilter !== 'all') queryParams.append('status', statusFilter);
    if (tagFilter !== 'all') queryParams.append('tag', tagFilter);
    if (searchTerm) queryParams.append('search', searchTerm);
    
    // Fetch announcements
    fetch(`https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-announcements-get?${queryParams}`)
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch announcements');
        return response.json();
      })
      .then(data => {
        setAnnouncements(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching announcements:', error);
        setNotification({
          show: true,
          message: 'Failed to load announcements',
          type: 'error'
        });
        setLoading(false);
      });
  }, [statusFilter, tagFilter, searchTerm]); // Re-fetch when filters change

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Open modal with empty form
  const openCreateModal = () => {
    resetForm();
    setEditingId(null);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Format dates for API
      const formattedData = {
        ...formData,
        publish_start: formData.publish_start ? new Date(formData.publish_start).toISOString() : null,
        publish_end: formData.publish_end ? new Date(formData.publish_end).toISOString() : null,
      };
      
      let response;
      let apiUrl;
      
      if (editingId) {
        // Update existing announcement
        apiUrl = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-announcements-edit';
        // Add the ID to the data payload
        formattedData.id = editingId;
        formattedData.updated_by = adminUser?.id || 1; // Add updated_by for edit operations
        
        response = await fetch(apiUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formattedData)
        });
      } else {
        // Create new announcement
        apiUrl = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-announcements-post';
        formattedData.created_by = adminUser?.id || 1; // Use the current admin user's ID
        
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formattedData)
        });
      }
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Failed to ${editingId ? 'update' : 'create'} announcement`);
      }
      
      // Refresh announcements list after successful submission
      const queryParams = new URLSearchParams();
      if (statusFilter !== 'all') queryParams.append('status', statusFilter);
      if (tagFilter !== 'all') queryParams.append('tag', tagFilter);
      if (searchTerm) queryParams.append('search', searchTerm);
      
      const refreshResponse = await fetch(`https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-announcements-get?${queryParams}`);
      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        setAnnouncements(refreshData);
      }
      
      // Show success notification
      setNotification({
        show: true,
        message: editingId ? 'Announcement updated successfully!' : 'Announcement created successfully!',
        type: 'success'
      });
      
      // Close modal and reset form
      closeModal();
      resetForm();
    } catch (error) {
      console.error(`Error ${editingId ? 'updating' : 'creating'} announcement:`, error);
      setNotification({
        show: true,
        message: error.message || `Failed to ${editingId ? 'update' : 'create'} announcement`,
        type: 'error'
      });
    }
  };

  // Reset form and state
  const resetForm = () => {
    setFormData({
      title: '',
      summary: '',
      body: '',
      tag: 'Update',
      image_url: '',
      publish_start: '',
      publish_end: '',
      status: 'draft',
      priority: 5
    });
  };

  // Handle edit button click
  const handleEdit = (id) => {
    const announcementToEdit = announcements.find(a => a.id === id);
    setFormData({
      ...announcementToEdit,
      publish_start: announcementToEdit.publish_start ? new Date(announcementToEdit.publish_start).toISOString().slice(0, 16) : '',
      publish_end: announcementToEdit.publish_end ? new Date(announcementToEdit.publish_end).toISOString().slice(0, 16) : ''
    });
    setEditingId(id);
    setIsModalOpen(true);
  };

  // Updated handleDelete function that opens the confirmation modal
  const handleDelete = (id) => {
    const announcement = announcements.find(a => a.id === id);
    setAnnouncementToDelete(announcement);
    setIsDeleteModalOpen(true);
  };

  // Function to handle the actual deletion after confirmation
  const confirmDelete = async () => {
    if (!announcementToDelete) return;
    
    try {
      // Send DELETE request to the API
      const response = await fetch(`https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-announcements-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: announcementToDelete.id })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete announcement');
      }
      
      // Update local state after successful deletion
      const filteredAnnouncements = announcements.filter(a => a.id !== announcementToDelete.id);
      setAnnouncements(filteredAnnouncements);
      
      setNotification({
        show: true,
        message: 'Announcement deleted successfully!',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting announcement:', error);
      setNotification({
        show: true,
        message: error.message || 'Failed to delete announcement',
        type: 'error'
      });
    }
    
    // Close modal and reset state
    setIsDeleteModalOpen(false);
    setAnnouncementToDelete(null);
  };

  // Get status class for styling
  const getStatusClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-instock';
      case 'draft':
      case 'pending':
        return 'status-lowstock';
      case 'scheduled':
        return 'status-instock';
      case 'archived':
        return 'status-outofstock';
      default:
        return '';
    }
  };

  // Get tag class for styling
  const getTagClass = (tag) => {
    switch (tag) {
      case 'Event':
        return 'tag-event';
      case 'Update':
        return 'tag-update';
      case 'Seasonal':
        return 'tag-seasonal';
      case 'Promo':
        return 'tag-promo';
      case 'Alert':
        return 'tag-alert';
      default:
        return '';
    }
  };

  // Sort announcements by priority (lower number = higher priority)
  const sortedAnnouncements = [...announcements].sort((a, b) => a.priority - b.priority);

  return (
    <div className="announcements-section">
      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <span className="notification-message">{notification.message}</span>
          <button 
            className="close-notification" 
            onClick={() => setNotification({ show: false, message: '', type: '' })}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Announcement Form Modal */}
      <AnnouncementFormModal
        isOpen={isModalOpen}
        onClose={closeModal}
        formData={formData}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        editingId={editingId}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        announcementTitle={announcementToDelete?.title || ''}
      />
      
      {/* Announcements Table Header */}
      <div className="inventory-actions">
        <div className="search-filter-container">
          <div className="search-container">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input 
              type="text" 
              className="search-input" 
              placeholder="Search announcements..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-container">
            <span className="filter-label">Category:</span>
            <select 
              className="category-filter" 
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              <option value="Event">Events</option>
              <option value="Update">Updates</option>
              <option value="Seasonal">Seasonal</option>
              <option value="Promo">Promotions</option>
              <option value="Alert">Alerts</option>
            </select>
          </div>
          
          <div className="filter-container">
            <span className="filter-label">Status:</span>
            <select 
              className="category-filter" 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="scheduled">Scheduled</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
        
        <button 
          className="add-product-btn" 
          onClick={openCreateModal}
        >
          New Announcement
        </button>
      </div>
      
      {/* Announcements Table */}
      <div className="inventory-table-container">
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Summary</th>
              <th>Status</th>
              <th>Publish Dates</th>
              <th>Priority</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '2rem 0' }}>
                  <div style={{ display: 'inline-block', width: '20px', height: '20px', border: '2px solid rgba(166, 124, 82, 0.2)', borderTop: '2px solid var(--dashboard-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: '8px', verticalAlign: 'middle' }}></div>
                  Loading announcements...
                </td>
              </tr>
            ) : sortedAnnouncements.length > 0 ? (
              sortedAnnouncements.map(announcement => (
                <tr key={announcement.id}>
                  <td className="product-name-cell">
                    <span className="product-name">{announcement.title}</span>
                    <span className="product-description">ID: {announcement.id}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${getTagClass(announcement.tag)}`}>
                      {announcement.tag}
                    </span>
                  </td>
                  <td>
                    <div className="product-description" style={{ maxWidth: '300px' }}>
                      {announcement.summary}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(announcement.status)}`}>
                      {announcement.status.charAt(0).toUpperCase() + announcement.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div>Start: {formatDate(announcement.publish_start)}</div>
                    <div>End: {formatDate(announcement.publish_end)}</div>
                  </td>
                  <td>{announcement.priority}</td>
                  <td>
                    <div className="table-actions">
                      <button 
                        className="edit-btn" 
                        onClick={() => handleEdit(announcement.id)}
                        title="Edit"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button 
                        className="delete-btn" 
                        onClick={() => handleDelete(announcement.id)}
                        title="Delete"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-results">No announcements found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default BucksAdminAnnouncements;